import 'isomorphic-fetch';
import { createErrorMessage } from '../messages';
import {
  CollectionName,
  WebsiteConversation,
  ConversationCheckAvailability,
  UserActionMessage,
  ReservationSystemProductInfo,
  DocumentObject,
  Conversation,
} from '@tomis-tech/types';

import { addBreadcrumb, logError, Category } from '../sentry-iframe';

/** Info to show on Check Availability carousel if error occurs */
export const ERROR_PRODUCT_INFO: ReservationSystemProductInfo = {
  id: '',
  name: 'Error loading tour name',
  description: 'Error loading tour description',
  price: 0,
  currency: 'USD',
  image_url: '',
};

/** New Check Availability function to get Product Info
 * for one product at a time, using a new endpoint Andrew
 * created to make check availability reservation system
 * agnostic: https://bitbucket.org/tomisteam/tomis-backend/pull-requests/1201
 */
export async function getProductV2(
  siteSlug: string,
  productId: string
): Promise<ReservationSystemProductInfo> {
  if (!siteSlug || !productId) {
    return ERROR_PRODUCT_INFO;
  }

  const fetchUrl = `https://app.tomis.tech/api/${siteSlug}/products/?product_id=${productId}`;

  return fetch(fetchUrl)
    .then(r => {
      if (r.ok) return r.json();
      throw new Error(r.status + r.statusText);
    })
    .then(async (data: ReservationSystemProductInfo) => {
      addBreadcrumb({
        category: Category.CheckAvailability,
        message: `Received response from ${fetchUrl}`,
        data,
      });

      /** Response body should be exactly what we need! */
      return data;
    })
    .catch(error => {
      logError(error, {
        category: Category.CheckAvailability,
        message: 'Error Get Product Information - Check Availability V2',
        level: 'error',
      });
      return ERROR_PRODUCT_INFO;
    });
}

type ProductAvailabilityReturnType = {
  [key: string]: {
    datetime: string;
    quantity: number;
    booking_url: string;
  }[];
};

/** Get Product Availability for new endpoint
 * created by Andrew. This will allow check availability
 * for all Rez systems using a unified API.
 *
 * @param {string} site_slug Site Slug in TOMIS API
 * @param {string[]} product_ids Array of product IDs
 * @param {string} date_start Format YYYY-MM-DD
 * @param {string} date_end Format YYY-MM-DD
 * @param {number} quantity Number of participants
 */
export const getProductAvailabilityV2 = async (
  site_slug: string,
  product_ids: string[],
  date_start: string,
  date_end: string,
  quantity: number
): Promise<{ error: boolean; availability: ProductAvailabilityReturnType }> => {
  const url = `https://app.tomis.tech/api/${site_slug}/products-availability/`;

  const requestBody = {
    product_ids,
    date_start,
    date_end,
    quantity,
  };

  return fetch(url, {
    method: 'POST',
    body: JSON.stringify(requestBody),
    headers: {
      'content-type': 'application/json',
    },
  })
    .then(r => {
      if (r.ok) return r.json();
      throw new Error(r.status + r.statusText);
    })
    .then((data: ProductAvailabilityReturnType) => {
      addBreadcrumb({
        category: Category.CheckAvailability,
        message: `Received response from ${url}`,
        data,
      });

      return { error: false, availability: data };
    })
    .catch(error => {
      logError(error, {
        category: Category.CheckAvailability,
        message: 'Error Get Product Availability',
        level: 'error',
        data: error,
      });

      const availability: ProductAvailabilityReturnType = {};
      product_ids.forEach(id => (availability[id] = []));

      return { error: true, availability };
    });
};

/** used by Date, Quantity, and Product selection components */
/**
 * - Save Check Availability parameters on Conversation document
 * - Add a UserAction Message to Messages collection
 * - Error handling (Sentry & error messages for users)
 */
export const updateConversationCheckAvailability = async ({
  conversation,
  updatedCheckAvailability,
  message,
}: {
  conversation: DocumentObject<WebsiteConversation | Conversation>;
  updatedCheckAvailability: ConversationCheckAvailability;
  message?: UserActionMessage;
}): Promise<{
  /** Error occurred when updated `checkAvailability` field on Conversation doc */
  error: boolean;
}> => {
  addBreadcrumb({
    message: 'Saving new checkAvailability parameters',
    data: updatedCheckAvailability,
  });

  // Update conversation, then add new userActionMessage
  return conversation.ref
    .update({
      checkAvailability: updatedCheckAvailability,
    })
    .then(() => {
      addBreadcrumb({
        message: 'Updated checkAvailability parameters',
      });

      // add new system message, non-fatal if fails
      if (message) {
        conversation.ref
          .collection(CollectionName.Messages)
          .add(message)
          .catch(logError);
      }
      return { error: false };
    })
    .catch(error => {
      logError(error);

      const firebaseErrorMessage = createErrorMessage(
        'Error saving selection, try again'
      );

      // add new system message, non-fatal if fails
      conversation.ref
        .collection(CollectionName.Messages)
        .add(firebaseErrorMessage)
        .catch(logError);

      return { error: true };
    });
};
